import { Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { AllProjectSampleTypesGroup, SampleTypeGroup } from '@resistapp/common/comparable-sample-groups';
import { friendlySampleType, SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { Chip, SamplingPoint } from '@resistapp/common/types';
import { uniq } from 'lodash';
import {
  getExactSelectedSubtypeBucket,
  getFriendlySampleSubtypeOrTypeLabel,
  getPresentSubtypeBucketsForType,
} from '../../../utils/sample-subtype-filter-utils';

export type SampleSearchOption =
  | {
      kind: 'all-samples';
      label: string;
      value: AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
    }
  | {
      kind: 'sample-type';
      label: string;
      value: SampleTypeGroup;
      sampleTypeGroup: SampleTypeGroup;
    }
  | {
      kind: 'sample-subtype';
      label: string;
      value: SampleSubType;
      sampleTypeGroup: SampleType;
      samplingPointIds: number[];
    }
  | {
      kind: 'chip';
      label: string;
      value: number;
    }
  | {
      kind: 'separator';
      label: string;
      value: string;
      isDisabled: true;
    };

export function buildSampleTypeScopeOptions(samplingPoints: SamplingPoint[]): SampleSearchOption[] {
  const existingSampleTypes = uniq(samplingPoints.map(samplingPoint => samplingPoint.type));

  return existingSampleTypes.flatMap(sampleType => [
    {
      kind: 'sample-type' as const,
      label: friendlySampleType(sampleType),
      value: sampleType,
      sampleTypeGroup: sampleType,
    },
    ...getPresentSubtypeBucketsForType(samplingPoints, sampleType).map(bucket => ({
      kind: 'sample-subtype' as const,
      label: getFriendlySampleSubtypeOrTypeLabel(undefined, bucket.subtype) ?? bucket.subtype,
      value: bucket.subtype,
      sampleTypeGroup: bucket.parentType,
      samplingPointIds: bucket.samplingPointIds,
    })),
  ]);
}

export function shouldShowSampleTypeScopeOptions(options: SampleSearchOption[]): boolean {
  const sampleTypeOptions = options.filter(option => option.kind === 'sample-type');
  const hasSubtypeOptions = options.some(option => option.kind === 'sample-subtype');
  return sampleTypeOptions.length > 1 || hasSubtypeOptions;
}

export function buildChipSearchOptions(chips: Chip[]): SampleSearchOption[] {
  return chips.map(chip => ({
    kind: 'chip' as const,
    label: `Chip ${chip.name}`,
    value: chip.id,
  }));
}

export function getSelectedSampleSearchOption(
  options: SampleSearchOption[],
  filters: Filters,
  samplingPoints: SamplingPoint[],
): SampleSearchOption {
  const chipOption = options.find(
    option => option.kind === 'chip' && filters.chips.length === 1 && filters.chips[0] === option.value,
  );
  if (chipOption) {
    return chipOption;
  }

  const selectedSubtypeBucket = getExactSelectedSubtypeBucket(
    filters.selectedSampleTypeGroup,
    filters.selectedSamplingPointIdsOrdered,
    samplingPoints,
  );
  if (selectedSubtypeBucket) {
    const subtypeOption = options.find(
      option =>
        option.kind === 'sample-subtype' &&
        option.value === selectedSubtypeBucket.subtype &&
        option.sampleTypeGroup === selectedSubtypeBucket.parentType,
    );
    if (subtypeOption) {
      return subtypeOption;
    }
  }

  return (
    options.find(option => {
      switch (option.kind) {
        case 'all-samples':
          return filters.selectedSampleTypeGroup === AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
        case 'sample-type':
          return filters.selectedSampleTypeGroup === option.sampleTypeGroup;
        default:
          return false;
      }
    }) ?? options[0]
  );
}
